import { useEffect } from "react";
import { useWidgetViewState } from "@salestrak/ui";
import { CurrentUserButton } from "#app/components/filters/current-user-button";
import { useCurrentUserFilterToggle } from "#app/lib/hooks/use-current-user-filter-toggle";
import { useDashboardScope } from "#app/widgets/dashboards/scope/dashboard-scope-context";
import { AnalyticsContext, WidgetViewEvent, trackEvent } from "#app/lib/analytics";

const trackOnlyMe = (enabled: boolean) =>
  trackEvent({
    context: AnalyticsContext.WidgetView,
    event: WidgetViewEvent.ToggleOnlyMe,
    data: { level: "widget", enabled },
  });

/**
 * The card header's Only-Me toggle: a transient, per-viewer switch that
 * narrows THIS card to the current user's projects. It publishes `onlyMe` into
 * the kit's `WidgetViewState`, and `useScopedWhere` folds it centrally as a
 * force-on over the dashboard toolbar's own Only-Me (off inherits), so no data
 * hook reads it by hand. Never persisted.
 *
 * The control seeds from the bag it writes: the header remounts when the card
 * crosses the fold into the "…" menu and back, and the bag outlives that move
 * on the card's provider, so the face must reopen on the position the body is
 * still narrowing by.
 *
 * Member-scoped surfaces (My Work) already narrow every card to the current
 * user, so the toggle would be a redundant no-op there and stays hidden.
 */
export function WidgetHeaderOnlyMe() {
  const { state, patch } = useWidgetViewState();
  const scope = useDashboardScope();
  const isMemberScope = scope?.entity?.type === "member";

  const {
    isCurrentUserButtonSelected,
    isCurrentUserFilterApplied,
    handleCurrentUserPress,
    handleCurrentUserClear,
  } = useCurrentUserFilterToggle({
    initialSelected: state.onlyMe === true,
    // The compact face has no clear ×, so the press itself is the off switch.
    toggleOnPress: true,
  });

  useEffect(() => {
    patch({ onlyMe: isCurrentUserFilterApplied });
  }, [isCurrentUserFilterApplied, patch]);

  if (isMemberScope) return null;
  return (
    <CurrentUserButton
      size="xs"
      compact
      selected={isCurrentUserButtonSelected}
      onClick={() => {
        trackOnlyMe(!isCurrentUserButtonSelected);
        handleCurrentUserPress();
      }}
      onClearClick={() => {
        trackOnlyMe(false);
        handleCurrentUserClear();
      }}
    />
  );
}
